import React, { useState } from "react";
import { OTelSpan } from "./types";
import { LLMSpanView } from "./views/LLMSpanView";
import { GenericSpanView } from "./views/GenericSpanView";
import { ToolSpanView } from "./views/ToolSpanView";
import { DefaultRunView } from "./views/DefaultRunView";
import { Clock, Cpu, Hash, AlertCircle } from "lucide-react";

interface SpanDetailProps {
  span: OTelSpan | null;
}

export const SpanDetail: React.FC<SpanDetailProps> = ({ span }) => {
  const [activeTab, setActiveTab] = useState<
    "Run" | "Attributes" | "Events" | "JSON"
  >("Run");

  if (!span) {
    return (
      <div className="flex items-center justify-center h-full w-full bg-white text-[#64748b] text-sm">
        Select a span to view details
      </div>
    );
  }

  const kind =
    span.attributes["openinference.span.kind"] ??
    (span.attributes["gen_ai.tool.name"] ? "TOOL" : undefined);
  const modelName =
    span.attributes["llm.model_name"] ??
    span.attributes["gen_ai.request.model"] ??
    span.attributes["model_name"];
  const tokens =
    span.attributes["llm.token_count.total"] ??
    (typeof span.attributes["gen_ai.usage.input_tokens"] === "number" &&
    typeof span.attributes["gen_ai.usage.output_tokens"] === "number"
      ? (span.attributes["gen_ai.usage.input_tokens"] as number) +
        (span.attributes["gen_ai.usage.output_tokens"] as number)
      : undefined);
  const status = span.status.code;

  const renderContent = () => {
    if (activeTab === "Attributes") {
      return <GenericSpanView span={span} />;
    }
    if (activeTab === "Events") {
      return (
        <div className="p-4 text-[#64748b] text-sm">
          {span.events.length > 0 ? (
            <div className="space-y-4">
              {span.events.map((e, i) => (
                <div
                  key={i}
                  data-testid="span-event"
                  data-event-name={e.name}
                  className="border border-[#e2e8f0] rounded-md p-3 bg-[#f8fafc]"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-[#0f172a]">{e.name}</span>
                    <span
                      data-testid="span-event-timestamp"
                      className="text-xs font-mono"
                    >
                      {e.timestamp && !isNaN(new Date(e.timestamp).getTime())
                        ? new Date(e.timestamp).toISOString()
                        : String(e.timestamp ?? "")}
                    </span>
                  </div>
                  <pre className="text-xs font-mono text-[#64748b] overflow-x-auto">
                    {JSON.stringify(e.attributes, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          ) : (
            "No events recorded."
          )}
        </div>
      );
    }
    if (activeTab === "JSON") {
      return (
        <div className="p-4">
          <pre
            data-testid="span-json"
            className="text-xs font-mono text-[#64748b] overflow-x-auto"
          >
            {JSON.stringify(span, null, 2)}
          </pre>
        </div>
      );
    }

    // 'Run' Tab — route by span kind or available attributes
    if (kind === "LLM") {
      return <LLMSpanView span={span} />;
    }
    if (kind === "TOOL") {
      return <ToolSpanView span={span} />;
    }
    // pydantic-ai tool spans (gen_ai.tool.name present)
    if (span.attributes["gen_ai.tool.name"]) {
      return <ToolSpanView span={span} />;
    }
    // Agent spans with pydantic_ai messages get the LLM view (shows conversation)
    if (
      span.attributes["pydantic_ai.all_messages"] ||
      span.attributes["llm.input_messages"]
    ) {
      return <LLMSpanView span={span} />;
    }
    return <DefaultRunView span={span} />;
  };

  return (
    <div
      data-testid="span-detail"
      data-span-id={span.id}
      className="flex flex-col h-full w-full bg-white overflow-hidden"
    >
      {/* Header */}
      <div className="flex flex-col px-5 pt-5 pb-3 border-b border-[#e2e8f0]">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <h2 className="text-[#0f172a] text-lg font-medium tracking-tight truncate">
              {span.name}
            </h2>
            {status === "ERROR" && (
              <span
                data-testid="span-error-badge"
                className="flex items-center gap-1 text-xs font-medium text-red-400 bg-red-400/10 px-2 py-0.5 rounded-full border border-red-400/20"
              >
                <AlertCircle className="w-3 h-3" />
                Error
              </span>
            )}
            {kind && (
              <span
                data-testid="span-kind-badge"
                className="text-[10px] uppercase tracking-wider font-semibold text-[#64748b] bg-[#e2e8f0] px-2 py-0.5 rounded-full"
              >
                {kind}
              </span>
            )}
          </div>
        </div>

        {/* Metrics Row */}
        <div className="flex items-center gap-4 text-xs text-[#64748b]">
          <div className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            <span className="font-mono">{span.durationMs.toFixed(2)}ms</span>
          </div>
          {modelName && (
            <div className="flex items-center gap-1.5">
              <Cpu className="w-3.5 h-3.5" />
              <span>{modelName}</span>
            </div>
          )}
          {tokens && (
            <div className="flex items-center gap-1.5">
              <Hash className="w-3.5 h-3.5" />
              <span className="font-mono">{tokens} tokens</span>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center px-5 border-b border-[#e2e8f0] bg-white">
        {["Run", "Attributes", "Events", "JSON"].map((tab) => (
          <button
            key={tab}
            data-testid={`span-tab-${tab.toLowerCase()}`}
            data-active={activeTab === tab}
            onClick={() => setActiveTab(tab as any)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab
                ? "text-[#0f172a] border-[#0f172a]"
                : "text-[#64748b] border-transparent hover:text-[#0f172a]"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto">{renderContent()}</div>
    </div>
  );
};
